import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Film, ArrowLeft, Loader2, UserPlus, Sparkles, Pencil, Trash2, RefreshCw, Check, X, Upload } from "lucide-react";

const VERTICALS = [
  { v: "real_estate", label: "Real Estate" },
  { v: "dealership", label: "Dealership" },
  { v: "photographer", label: "Photographer" },
  { v: "general", label: "General" },
];
const vlabel = (v) => VERTICALS.find((x) => x.v === v)?.label || "General";

export default function Agents() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [chars, setChars] = useState([]);
  const [loading, setLoading] = useState(true);

  // create form
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [vert, setVert] = useState("real_estate");
  const [creating, setCreating] = useState(false);
  const [createMode, setCreateMode] = useState("generate"); // "generate" | "upload"
  const [photo, setPhoto] = useState(null);

  // edit state
  const [editId, setEditId] = useState(null);
  const [eName, setEName] = useState("");
  const [eDesc, setEDesc] = useState("");
  const [eVert, setEVert] = useState("real_estate");
  const [savingId, setSavingId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    api.get("/characters").then((r) => setChars(r.data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  const portrait = (c) => (c.portrait_media_id ? mediaUrl(c.portrait_media_id) : c.portrait_url || "");

  const createAgent = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (createMode === "upload") {
      if (!photo) return toast.error("Choose a photo to upload");
      setCreating(true);
      try {
        const fd = new FormData();
        fd.append("name", name.trim());
        fd.append("description", desc.trim());
        fd.append("vertical", vert);
        fd.append("image", photo);
        await api.post("/characters/upload", fd);
        toast.success("Agent created from your photo");
        setName(""); setDesc(""); setPhoto(null);
        load();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setCreating(false);
      }
      return;
    }
    if (!desc.trim()) return toast.error("Name and description are required");
    setCreating(true);
    try {
      await api.post("/characters", { name: name.trim(), description: desc.trim(), aspect: "9:16", vertical: vert });
      toast.success("Generating agent portrait…");
      setName(""); setDesc("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (c) => {
    setEditId(c.id); setEName(c.name); setEDesc(c.description || ""); setEVert(c.vertical || "real_estate");
  };
  const cancelEdit = () => setEditId(null);

  const saveEdit = async (id) => {
    if (!eName.trim() || !eDesc.trim()) return toast.error("Name and description are required");
    setSavingId(id);
    try {
      await api.put(`/characters/${id}`, { name: eName.trim(), description: eDesc.trim(), vertical: eVert });
      toast.success("Agent updated. Regenerate the portrait if you changed the look.");
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingId(null);
    }
  };

  const regenerate = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/characters/${id}/regenerate`, { aspect: "9:16" });
      toast.success("Regenerating portrait…");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete agent "${c.name}"? This can't be undone.`)) return;
    setBusyId(c.id);
    try {
      await api.delete(`/characters/${c.id}`);
      toast.success("Agent deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="agents-page">
      <header className="sticky top-0 z-20 bg-zinc-950/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button data-testid="agents-back" onClick={() => navigate("/app")} className="flex items-center gap-2 text-amber-500 hover:text-amber-400 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <Film className="w-5 h-5" />
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-zinc-200">Agents</span>
          </button>
          <span className="text-sm text-zinc-500 hidden sm:block">{user?.email}</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 text-amber-500 mb-2">
          <Sparkles className="w-4 h-4" />
          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em]">Manage Your Virtual Agents</span>
        </div>
        <p className="text-sm text-zinc-500 mb-8 max-w-2xl">
          Create and edit agents here, then cast them into any reel. Editing the description won't change the portrait until you press <span className="text-amber-400">Regenerate portrait</span> (that step uses credits).
        </p>

        {/* Create */}
        <section className="mb-12 border border-zinc-800 bg-zinc-900/50 rounded-sm p-6" data-testid="agent-create-card">
          <h2 className="font-display text-lg font-medium mb-4">Create a new agent</h2>
          <div className="flex gap-2 mb-5">
            <button
              data-testid="agent-mode-generate"
              onClick={() => setCreateMode("generate")}
              className={`flex-1 text-left px-3 py-2.5 rounded-sm border transition-colors ${createMode === "generate" ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-zinc-700 text-zinc-400 hover:border-zinc-600"}`}
            >
              <span className="block text-sm font-medium">Generate from description</span>
              <span className="block text-[10px] text-zinc-500">AI creates the portrait (uses credits)</span>
            </button>
            <button
              data-testid="agent-mode-upload"
              onClick={() => setCreateMode("upload")}
              className={`flex-1 text-left px-3 py-2.5 rounded-sm border transition-colors ${createMode === "upload" ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-zinc-700 text-zinc-400 hover:border-zinc-600"}`}
            >
              <span className="block text-sm font-medium">Upload my own photo</span>
              <span className="block text-[10px] text-zinc-500">Use a real photo of you or your agent</span>
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">Name</label>
              <Input data-testid="agent-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ava Bennett" className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11" />
              <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-4 block">Vertical</label>
              <select data-testid="agent-vertical-select" value={vert} onChange={(e) => setVert(e.target.value)} className="mt-2 w-full h-11 rounded-sm bg-zinc-950 border border-zinc-800 text-sm text-zinc-200 px-3 focus:border-amber-500 outline-none">
                {VERTICALS.map((x) => <option key={x.v} value={x.v}>{x.label}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                {createMode === "upload" ? "Appearance notes (optional)" : "Appearance description"}
              </label>
              <Textarea data-testid="agent-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={createMode === "upload" ? "Optional: outfit / vibe notes to help scene generation" : "a professional female real estate agent in a black blazer, mid 30s, confident and warm"} className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm min-h-[104px]" />
              {createMode === "upload" && (
                <div className="mt-3">
                  <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">Your photo</label>
                  <label data-testid="agent-photo-upload" className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300 border border-dashed border-zinc-700 hover:border-amber-500 rounded-sm px-3 py-2.5 transition-colors">
                    <Upload className="w-4 h-4 text-amber-500" />
                    {photo ? photo.name : "Choose a clear, front-facing photo"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
                  </label>
                  {photo && (
                    <img src={URL.createObjectURL(photo)} alt="preview" className="mt-3 w-24 h-24 object-cover rounded-sm border border-zinc-700" />
                  )}
                </div>
              )}
            </div>
          </div>
          <Button data-testid="agent-create-button" onClick={createAgent} disabled={creating} className="mt-4 h-11 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1.5" /> Create agent</>}
          </Button>
        </section>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agents-grid">
            {chars.map((c) => (
              <div key={c.id} data-testid={`agent-item-${c.id}`} className="border border-zinc-800 bg-zinc-900/40 rounded-sm overflow-hidden">
                <div className="aspect-[4/3] bg-zinc-900 overflow-hidden relative">
                  {portrait(c) ? (
                    <img src={portrait(c)} alt={c.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {c.status === "generating" ? <Loader2 className="w-6 h-6 text-amber-500 animate-spin" /> : <UserPlus className="w-7 h-7 text-zinc-700" />}
                    </div>
                  )}
                  {c.status === "generating" && (
                    <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest bg-amber-500/90 text-zinc-950 px-2 py-0.5 rounded-sm">Rendering…</span>
                  )}
                  {c.is_default && (
                    <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest bg-zinc-800/90 text-zinc-300 px-2 py-0.5 rounded-sm">Default</span>
                  )}
                </div>

                <div className="p-4">
                  {editId === c.id ? (
                    <div className="space-y-2" data-testid={`agent-edit-${c.id}`}>
                      <Input data-testid={`agent-edit-name-${c.id}`} value={eName} onChange={(e) => setEName(e.target.value)} className="bg-zinc-950 border-zinc-800 rounded-sm h-9 text-sm" />
                      <Textarea data-testid={`agent-edit-desc-${c.id}`} value={eDesc} onChange={(e) => setEDesc(e.target.value)} className="bg-zinc-950 border-zinc-800 rounded-sm min-h-[80px] text-sm" />
                      <select value={eVert} onChange={(e) => setEVert(e.target.value)} className="w-full h-9 rounded-sm bg-zinc-950 border border-zinc-800 text-sm text-zinc-200 px-2 focus:border-amber-500 outline-none">
                        {VERTICALS.map((x) => <option key={x.v} value={x.v}>{x.label}</option>)}
                      </select>
                      <div className="flex gap-2 pt-1">
                        <Button data-testid={`agent-save-${c.id}`} onClick={() => saveEdit(c.id)} disabled={savingId === c.id} size="sm" className="h-8 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400">
                          {savingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Save</>}
                        </Button>
                        <Button data-testid={`agent-cancel-${c.id}`} onClick={cancelEdit} size="sm" variant="ghost" className="h-8 rounded-sm text-zinc-400 hover:text-white">
                          <X className="w-3.5 h-3.5 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-display text-base font-medium truncate">{c.name}</h3>
                        <span className="text-[9px] uppercase tracking-widest text-zinc-500 shrink-0">{vlabel(c.vertical)}</span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1 line-clamp-3">{c.description}</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Button data-testid={`agent-edit-btn-${c.id}`} onClick={() => startEdit(c)} size="sm" className="h-8 rounded-sm bg-zinc-800 text-zinc-100 hover:bg-zinc-700">
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                        </Button>
                        <Button data-testid={`agent-regen-btn-${c.id}`} onClick={() => regenerate(c.id)} disabled={busyId === c.id || c.status === "generating"} size="sm" className="h-8 rounded-sm bg-zinc-800 text-amber-300 hover:bg-zinc-700 disabled:opacity-50">
                          {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate</>}
                        </Button>
                        {!c.is_default && (
                          <Button data-testid={`agent-delete-btn-${c.id}`} onClick={() => remove(c)} disabled={busyId === c.id} size="sm" variant="ghost" className="h-8 rounded-sm text-red-400 hover:text-red-300 hover:bg-red-500/10">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
